<template>

    <div class="teacher-feedback-wrap">

        <loading v-if="loading"></loading>

        <template v-else>
            <div class="page-content teacher-feedback-container">

              <a href="#" @click.prevent="showReflectiontips(assignment)" class="button-rounded button-rounded--reflection button-rounded--teachertips" v-if="shouldShowReflectiontips"><span class="icon icon--reflection"></span>Tips</a>

                <h2 class="teacher-feedback__title">Feedback</h2>

                <div class="input-group" v-if="compliments.length">
                    <h3>Dit heb je goed gedaan</h3>

                    <multiselect v-model="feedback.compliments" :options="compliments" track-by="id" label="content" :searchable="false" :close-on-select="true" :show-labels="false" :multiple="true" :hide-selected="true" :disabled="hasFeedbackFromOtherTeacher" placeholder="Kies (één of meer) tops..."></multiselect>

                </div>

                <div class="input-group" v-if="remarks.length">
                    <h3>Besteed hier nog wat aandacht aan</h3>

                    <multiselect v-model="feedback.remarks" :options="remarks" track-by="id" label="content" :searchable="false" :close-on-select="true" :show-labels="false" :multiple="true" :hide-selected="true" :disabled="hasFeedbackFromOtherTeacher" placeholder="Kies (één of meer) tips..."></multiselect>
                </div>

                <h3>Opmerkingen</h3>
                <textarea class="teacher-feedback" v-model="feedback.content" placeholder="" :readonly="hasFeedbackFromOtherTeacher"></textarea>

                <h2>Beoordeling</h2>
                <Diligence :progress="progress" :isDemo="isDemo" @input="diligence = $event" />

                <p v-if="progress.isRetaking" class="note note--large note--warning"><span class="icon icon--inline icon--retake-dark"></span> Deze opdracht wordt overgedaan.</p>
                <p v-else><a href="#" @click="retakeAssignment" class="tag button-timid button--retake-assignment"><span class="icon icon--inline icon--retake-dark"></span> Laat {{ reviewee.firstname }} opdracht overdoen</a></p>

                <Badges :progress="progress" :isDemo="isDemo" :disabled="diligence !== 'sufficient'"/>

            </div>

            <nav class="action-nav">
                <hr class="flipped">
                <router-link :to="{ name: 'group.assignment.group', params: { periodId: periodIdForAssignment(assignment) } }" class="show-group-submissions tag button-timid button-forward">Klasoverzicht {{ assignment.title }}</router-link>
                <primary-button :link="returnLink.route" class="action-nav__next" >Klaar</primary-button>
            </nav>

        </template>

    </div>

</template>


<script>

import PrimaryButton from 'js/components/elements/PrimaryButton.vue';
import Loading from 'js/components/elements/Loading.vue';
import Multiselect from 'vue-multiselect';
import Diligence from 'js/components/assignment/Diligence.vue';
import Badges from 'js/components/assignment/Badges.vue';

import api from 'js/api/resources';
import { transform, debounceSync, guid } from 'js/vuex/helpers';
import { getGroupTermsForGroup } from 'js/vuex/getters';
import notify from 'js/components/notifications/notificationsMixin';
import track from 'js/helpers/analytics';


// FEEDBACK STORAGE OUTLINE
// put vuex stored feedback into data
// watch data change
// -> storeFeedback() -> syncFeedback()
// ---> dbsync() // debounced api call + store to vuex
// ---> flush if new feedback (send to api as fast as possible)
// ---> store to vuex if not new (we know the id)


/**
 * Debounced api sync, waits for previous sync before starting the next
 * Defined outside of vue object to be able to call flush/cancel
 */
const dbsync = debounceSync( function(state, progress, feedback ) {

    // checks at the time of execution if the oldValue (thus the id) is known
    // note that not more than one of these functions is executed at the same time (guarenteed by debounceSync)
    // therefore the 'post' api action is executed only once (unless multiple browsers etc. etc.)
    const oldValue = progress.feedback == null ? null : state.feedback.storage[progress.feedback[0]]

    return oldValue == null ?
            api.addFeedback(progress.id, feedback.content, feedback.compliments, feedback.remarks)
            : api.patchFeedback(oldValue.id, feedback.content, feedback.compliments, feedback.remarks)

}, 6000, { maxWait: 60000 })


export default {
    name: 'TeacherFeedback',
    props: {
        progress: {},
        assignment: {},
        group: {},
        reviewee: {},
        isDemo: {
            type: Boolean,
            default: false
        },
        returnLink: {
            type: Object,
            required: true
        }

    },
    mixins: [ notify ],
    components: { PrimaryButton, Badges, Loading, Multiselect, Diligence },
    data: function() {
        return {
            loading: false,
            feedback: {
                content: '',
                compliments: [],
                remarks: [],
            },
            diligence: null,
        }
    },

    vuex: {
        getters: {
            getGroupTermsForGroup,
        },
        actions: {
            showReflectiontips: ({dispatch}, assignment) => {
                track.event('reflectiontips', 'show', assignment.id)
                dispatch('SHOW_MODAL', 'Reflectiontips')
            },

            syncFeedback: ({dispatch, state}, progress, feedback, isDemo, teacher) => {

                const oldValue = progress.feedback == null ? null : state.feedback.storage[progress.feedback[0]]

                if ( isDemo ) {
                    // fake store feedback to vuex
                    const id = oldValue == null ? guid() : oldValue.id

                    dispatch('ADD_FEEDBACK', [Object.assign({}, {id: id, teacher: [teacher.id]}, feedback)])
                    dispatch('ADD_FEEDBACK_TO_PROGRESS', progress.id, id)

                    return Promise.resolve()
                }

                // schedule sync to api
                const result = dbsync(state, progress, feedback)
                    .then( response => transform(response.body) )
                    .then( result => {
                        dispatch('ADD_FEEDBACK', result.feedback)
                        dispatch('ADD_FEEDBACK_TO_PROGRESS', progress.id, result.feedback[0].id)
                    })

                // flush if new feedback (send to api as fast as possible)
                // store to vuex if not new (we know the id)
                if ( oldValue == null ) {
                    dbsync.flush()
                } else {
                    dispatch('ADD_FEEDBACK', [Object.assign({}, {id: oldValue.id}, feedback)] )
                }

                return result

            },
            retakeProgress: ({dispatch}, progress) => {
                return api.retakeProgress(progress.id)
                    .then( response => transform(response.body) )
                    .then( result => dispatch('ADD_PROGRESS', result.progress) )
            }
        }
    },

    computed: {
        storedFeedback: function() {
            if ( this.progress.feedback == null || this.progress.feedback.length === 0 ) {
                return null
            }

            return this.$store.state.feedback.storage[this.progress.feedback[0]]
        },
        remarks: function() {
            return this.assignment.remarks.map( t => this.$store.state.feedback.remarks[t] )
        },
        compliments: function() {
            return this.assignment.compliments.map( t => this.$store.state.feedback.compliments[t] )
        },


        loggedInUser: function() {
            return this.$store.state.users.current
        },

        hasFeedbackFromOtherTeacher: function() {

            // if feedback is empty, no other teacher has created feedback yet
            if ( this.storedFeedback == null || this.storedFeedback.teacher.length === 0 ) {
                return false
            }

            return this.storedFeedback.teacher[0] !== this.loggedInUser.id
        },

        shouldShowReflectiontips: function() {
            return this.assignment.reflectiontips === true
        },

    },

    methods: {

        periodIdForAssignment: function(assignment) {
            const groupPeriod = this.getGroupTermsForGroup(this.group.id)
                .reduce( (list, t) => list.concat(t.groupPeriods), [])
                .map( id => this.$store.state.groups.periods[id] )
                .find( gp => {
                    return gp.groupAssignments
                        .map(id => this.$store.state.groups.assignments[id])
                        .find( ga =>
                            ga.assignment[0] === assignment.id
                        )
                })

            if (groupPeriod == null) {
                return null
            }

            return groupPeriod.id
        },

        setFeedbackFromStore: function() {

            if ( this.storedFeedback != null ) {

                this.feedback.content = this.storedFeedback.content
                this.feedback.remarks = this.storedFeedback.remarks.map( r => this.$store.state.feedback.remarks[r] )
                this.feedback.compliments = this.storedFeedback.compliments.map( r => this.$store.state.feedback.compliments[r] )
            }

        },

        convertFeedbackForApi: function(feedback) {

            return {
                content: feedback.content,
                compliments: feedback.compliments.map( c => c.id ),
                remarks: feedback.remarks.map( r => r.id ),
            }

        },

        storeFeedback: function(newValue) {
            this.syncFeedback(this.progress, this.convertFeedbackForApi(newValue), this.isDemo, this.loggedInUser)
                .catch( err => {
                    track.exception('Can\'t store feedback', err)
                    this.$warn('Kan feedback niet opslaan')
                })
        },

        retakeAssignment: function() {

            this.$alert('Weet je zeker dat ' + this.reviewee.firstname + ' deze opdracht moet overdoen?', [
                { name: 'Annuleren' },
                { name: 'Overdoen', primary: true, action: () => {

                    track.event('assignment', 'retake', this.progress.assignment)
                    this.retakeProgress(this.progress)
                        .catch( err => {
                            track.exception('Can\'t retake assignment', err)
                            this.$error('De opdracht kan niet worden overgedaan')
                        })

                }}
            ])

        },

    },

    created: function() {
        this.setFeedbackFromStore()
        this.$watch( 'feedback', this.storeFeedback, { deep: true })
    },

    beforeDestroy: function() {
        dbsync.flush()
    }

}
</script>


<style lang="scss">

    @import 'global';
    @import 'multiselect';

    .teacher-feedback-wrap {
        padding-right: 50%;
        margin-right: -50%;
        padding-left: 50%;
        margin-left: -50%;
        background-color: #f6f6f6;
    }

    .teacher-feedback-container {
        padding-top: 12%;
        padding-bottom: 3%;
    }

    .teacher-feedback__title {
      margin-top: 0;
    }

</style>



// WEBPACK FOOTER //
// TeacherFeedback.vue?1bff1757